import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { debounce } from "lodash";
import {
	Button,
	Col,
	Form,
	Input,
	Modal,
	PageHeader,
	Row,
	Select,
	Table,
	Tooltip,
} from "antd";
import moment from "moment-jalaali";

import {
	CheckPermission,
	GetUrlParams,
	MinuteFormat,
	ObjectToURL,
} from "scripts/helpers";
import { setRoute } from "scripts/reducer";
import { Modals, Permissions } from "scripts/settings";
import API from "scripts/api";
import Resources from "scripts/resources";

import FormModal from "modals/FilesForm";

const Component = ({ onSetRoute, parentName, permissions, routeName }) => {
	const initialState = { data: [], pagination: {} };
	const tableColumns = [
		{
			title: Resources.row,
			dataIndex: "index",
			className: "center-cell",
			width: "60px",
			render: (value, record, index) =>
				(pagination.current - 1) * pagination.pageSize + index + 1,
		},
		{
			title: Resources.title,
			dataIndex: "title",
		},
		{
			title: Resources.description,
			dataIndex: "description",
			render: (value) => value?.slice(0, 90),
		},

		{
			title: Resources.extension,
			dataIndex: "extension",
			className: "center-cell ltr-cell",
			width: "80px",
		},
		{
			title: `${Resources.date} ${Resources.record}`,
			dataIndex: "createdAt",
			className: "center-cell desc-cell ltr-cell",
			width: "120px",
			render: (value) => {
				const [date, time] = moment(value)
					.format(process.env.REACT_APP_DATE_TIME_FORMAT_JALALI)
					.split(" ");

				return (
					<>
						<span className="name">{date}</span>
						<small className="desc">{time}</small>
					</>
				);
			},
		},
		{
			title: Resources.action,
			dataIndex: "id",
			className: "center-cell",
			width: "120px",
			render: (value, record) => (
				<>
					{permissions.update && (
						<Tooltip title={Resources.edit}>
							<Button
								type="link"
								icon={<i className="fa fa-pencil-alt" />}
								onClick={() => handleForm(record)}
							/>
						</Tooltip>
					)}
					{permissions.delete && (
						<Tooltip title={Resources.delete}>
							<Button
								type="link"
								icon={<i className="fa fa-trash-alt" />}
								onClick={() => handleDelete(value)}
							/>
						</Tooltip>
					)}
				</>
			),
		},
	];
	const [state, setState] = useState(initialState);
	const { data, loading, pagination, selected } = state;
	const { pathname, search } = useLocation();
	const [form] = Form.useForm();
	const navigate = useNavigate();

	const fetchData = () => {
		const params = GetUrlParams(search);

		form.resetFields();
		form.setFieldsValue(params);

		setState((prevState) => ({ ...prevState, loading: true }));

		API.files
			.list({ ...params, pageSize: process.env.REACT_APP_PAGE_SIZE })
			.then(({ data, meta: { currentPage, perPage, total } }) => {
				setState((prevState) => ({
					...prevState,
					data,
					pagination: {
						current: currentPage,
						pageSize: perPage,
						total,
						showSizeChanger: false,
					},
					loading: false,
				}));
			})
			.catch(() => {
				setState((prevState) => ({ ...prevState, loading: false }));
			});
	};

	const handleForm = (selected, reload) => {
		if (reload) {
			fetchData();
		} else {
			setState((prevState) => ({ ...prevState, selected }));

			navigate(
				`${pathname}${search}#${selected ? Modals.EDIT : Modals.CREATE}`
			);
		}
	};

	const handleDelete = (id) => {
		Modal.confirm({
			title: Resources.deleteConfirm,
			okText: Resources.yes,
			okType: "danger",
			cancelText: Resources.no,
			onOk: () => {
				setState((prevState) => ({ ...prevState, loading: true }));

				API.files
					.delete(id)
					.then(() => {
						fetchData();
					})
					.catch(() => {
						setState((prevState) => ({ ...prevState, loading: false }));
					});
			},
		});
	};

	const handleTable = ({ current }) => {
		const params = GetUrlParams(search);
		const _search = ObjectToURL({ ...params, page: current });

		navigate({ pathname, search: _search });
	};

	const handleFilter = debounce(() => {
		form.validateFields().then((values) => {
			const search = ObjectToURL({ page: 1, ...values });

			navigate({ pathname, search });
		});
	}, 1000);

	const componentDidUpdate = () => {
		fetchData();
	};

	const componentDidMount = () => {
		onSetRoute({ openKey: parentName, selectedKey: routeName });
	};

	useEffect(componentDidUpdate, [search]);
	useEffect(componentDidMount, []);

	return (
		<>
			<PageHeader
				title={`${Resources.file}${Resources.ha}`}
				extra={
					permissions.create && (
						<Button
							type="primary"
							onClick={() => handleForm()}
							loading={loading}
						>
							{Resources.add}
						</Button>
					)
				}
			/>

			<Form
				layout="vertical"
				form={form}
			>
				<Row gutter={24}>
					<Col
						xs={24}
						sm={12}
						md={8}
						lg={6}
						xxl={4}
					>
						<Form.Item name="search">
							<Input
								suffix={<i className="fa fa-search" />}
								onChange={handleFilter}
								placeholder={Resources.search}
								allowClear
								autoFocus
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>

			<Table
				columns={tableColumns}
				dataSource={data}
				loading={loading}
				onChange={handleTable}
				pagination={pagination}
				scroll={{ x: 800 }}
				rowKey="id"
			/>
			<FormModal
				data={selected}
				onUpdate={handleForm}
			/>
		</>
	);
};

export default connect(
	(state) => {
		return {
			permissions: {
				create: CheckPermission(
					state.user.permissions,
					Permissions.FILES_CREATE
				),
				delete: CheckPermission(
					state.user.permissions,
					Permissions.FILES_DELETE
				),
				update: CheckPermission(
					state.user.permissions,
					Permissions.FILES_UPDATE
				),
				status: CheckPermission(
					state.user.permissions,
					Permissions.FILES_STATUS
				),
			},
		};
	},
	(dispatch) => {
		return {
			onSetRoute: (data) => dispatch(setRoute(data)),
		};
	}
)(Component);
